import { ArrowLeft } from "lucide-react";
import { useState } from "react";

interface PropertyDetails {
  propertyName: string;
  location: string;
  size: string;
  footfall: string;
  businessAge: string;
  currentOfferings: string;
}

interface Props {
  businessType: string;
  challenges: string[];
  onDetailsSubmit: (details: PropertyDetails) => void;
  onBack: () => void;
}

export default function PropertySpecifics({
  businessType,
  challenges,
  onDetailsSubmit,
  onBack,
}: Props) {
  const [propertyDetails, setPropertyDetails] = useState<PropertyDetails>({
    propertyName: "",
    location: "",
    size: "",
    footfall: "",
    businessAge: "",
    currentOfferings: "",
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    const newErrors: { [key: string]: string } = {};

    if (!propertyDetails.propertyName.trim()) {
      newErrors.propertyName = "Property name is required";
    }
    if (!propertyDetails.location.trim()) {
      newErrors.location = "Location is required";
    }
    if (!propertyDetails.size.trim()) {
      newErrors.size = "Property size is required";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      onDetailsSubmit(propertyDetails);
    }
  };

  const handleInputChange = (field: keyof PropertyDetails, value: string) => {
    setPropertyDetails((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  // Get business type display name
  const getBusinessDisplayName = (type: string) => {
    const businessMap: { [key: string]: string } = {
      property: "Property Management",
      hospitality: "Hospitality",
      retail: "Retail",
    };
    return businessMap[type] || type;
  };

  const isFormValid =
    propertyDetails.propertyName.trim() &&
    propertyDetails.location.trim() &&
    propertyDetails.size.trim();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-pink-900/20 to-slate-900 text-white relative overflow-hidden">
      {/* Animated background elements - Mobile optimized */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-1/4 left-1/4 w-28 h-28 sm:w-36 sm:h-36 bg-pink-500 rounded-full filter blur-3xl animate-pulse delay-500"></div>
        <div className="absolute bottom-1/4 right-1/3 w-24 h-24 sm:w-32 sm:h-32 bg-orange-500 rounded-full filter blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-2/3 left-1/2 w-20 h-20 sm:w-28 sm:h-28 bg-purple-500 rounded-full filter blur-3xl animate-pulse"></div>
      </div>

      {/* Scrollable content */}
      <div className="relative z-10 min-h-screen p-3 sm:p-4 md:p-8">
        <div className="max-w-4xl mx-auto">
          {/* Header - Mobile optimized */}
          <div className="text-center mb-6 sm:mb-8 md:mb-12 pt-4 sm:pt-8">
            {/* Breadcrumb with context - Mobile responsive */}
            <div className="inline-flex items-center bg-pink-500/20 backdrop-blur-sm rounded-full px-3 py-2 sm:px-4 mb-6 sm:mb-8 border border-pink-500/30">
              <div className="w-2 h-2 bg-pink-400 rounded-full animate-pulse mr-2 sm:mr-3"></div>
              <span className="text-pink-400 text-xs sm:text-sm font-medium">
                {getBusinessDisplayName(businessType).toUpperCase()} •{" "}
                {challenges.length} CHALLENGES • STEP 3 OF 4
              </span>
            </div>

            {/* Mobile-first heading */}
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black mb-4 sm:mb-6 bg-gradient-to-r from-white to-slate-300 bg-clip-text text-transparent leading-tight">
              Tell us about your property
            </h2>
            <p className="text-lg sm:text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed px-2">
              The more specific we can be, the better our cultural analysis and
              recommendations will be.
            </p>
          </div>

          {/* Form - Mobile-first layout */}
          <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
            {/* Main form container */}
            <div className="bg-slate-800/40 backdrop-blur-sm rounded-2xl p-4 sm:p-6 md:p-8 border border-slate-600/50">
              {/* Mobile: Single column, Desktop: Two columns */}
              <div className="space-y-6 md:grid md:grid-cols-2 md:gap-8 md:space-y-0">
                {/* Required fields section */}
                <div className="space-y-6">
                  <div className="text-sm text-slate-400 font-medium mb-4 md:col-span-2">
                    Required Information
                  </div>

                  {/* Property Name */}
                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-3">
                      Property Name *
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. The Exchange Building"
                      value={propertyDetails.propertyName}
                      onChange={(e) =>
                        handleInputChange("propertyName", e.target.value)
                      }
                      className={`w-full bg-slate-700/50 border rounded-xl px-4 py-3 sm:py-4 text-white placeholder-slate-400 focus:outline-none focus:ring-2 transition-all text-base ${
                        errors.propertyName
                          ? "border-red-500 focus:ring-red-500/50"
                          : "border-slate-600/50 focus:border-pink-400 focus:ring-pink-500/30"
                      }`}
                    />
                    {errors.propertyName && (
                      <p className="text-red-400 text-sm mt-2">
                        {errors.propertyName}
                      </p>
                    )}
                  </div>

                  {/* Location */}
                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-3">
                      Location *
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Manchester City Centre"
                      value={propertyDetails.location}
                      onChange={(e) =>
                        handleInputChange("location", e.target.value)
                      }
                      className={`w-full bg-slate-700/50 border rounded-xl px-4 py-3 sm:py-4 text-white placeholder-slate-400 focus:outline-none focus:ring-2 transition-all text-base ${
                        errors.location
                          ? "border-red-500 focus:ring-red-500/50"
                          : "border-slate-600/50 focus:border-pink-400 focus:ring-pink-500/30"
                      }`}
                    />
                    {errors.location && (
                      <p className="text-red-400 text-sm mt-2">
                        {errors.location}
                      </p>
                    )}
                  </div>

                  {/* Size */}
                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-3">
                      Size *
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. 50,000 sq ft"
                      value={propertyDetails.size}
                      onChange={(e) =>
                        handleInputChange("size", e.target.value)
                      }
                      className={`w-full bg-slate-700/50 border rounded-xl px-4 py-3 sm:py-4 text-white placeholder-slate-400 focus:outline-none focus:ring-2 transition-all text-base ${
                        errors.size
                          ? "border-red-500 focus:ring-red-500/50"
                          : "border-slate-600/50 focus:border-pink-400 focus:ring-pink-500/30"
                      }`}
                    />
                    {errors.size && (
                      <p className="text-red-400 text-sm mt-2">{errors.size}</p>
                    )}
                  </div>
                </div>

                {/* Optional fields section */}
                <div className="space-y-6">
                  <div className="text-sm text-slate-400 font-medium mb-4">
                    Optional Details
                  </div>

                  {/* Footfall */}
                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-3">
                      Daily Footfall
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. 1,000+ visitors per day"
                      value={propertyDetails.footfall}
                      onChange={(e) =>
                        handleInputChange("footfall", e.target.value)
                      }
                      className="w-full bg-slate-700/50 border border-slate-600/50 rounded-xl px-4 py-3 sm:py-4 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:border-pink-400 focus:ring-pink-500/30 transition-all text-base"
                    />
                  </div>

                  {/* Business Age */}
                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-3">
                      How long established?
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. 5 years"
                      value={propertyDetails.businessAge}
                      onChange={(e) =>
                        handleInputChange("businessAge", e.target.value)
                      }
                      className="w-full bg-slate-700/50 border border-slate-600/50 rounded-xl px-4 py-3 sm:py-4 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:border-pink-400 focus:ring-pink-500/30 transition-all text-base"
                    />
                  </div>

                  {/* Current Offerings */}
                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-3">
                      Current Cultural Offerings
                    </label>
                    <textarea
                      placeholder="e.g. Art exhibitions, community events..."
                      rows={4}
                      value={propertyDetails.currentOfferings}
                      onChange={(e) =>
                        handleInputChange("currentOfferings", e.target.value)
                      }
                      className="w-full bg-slate-700/50 border border-slate-600/50 rounded-xl px-4 py-3 sm:py-4 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:border-pink-400 focus:ring-pink-500/30 transition-all resize-none text-base"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Selected challenges preview - Mobile optimized */}
            <div className="bg-slate-800/30 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-slate-600/30">
              <h3 className="text-lg font-semibold text-white mb-4">
                Selected Challenges
              </h3>
              <div className="flex flex-wrap gap-2">
                {challenges.map((challengeId) => (
                  <span
                    key={challengeId}
                    className="px-3 py-1 bg-pink-500/20 text-pink-300 text-xs sm:text-sm rounded-full border border-pink-500/30"
                  >
                    {challengeId
                      .replace("-", " ")
                      .replace(/\b\w/g, (l) => l.toUpperCase())}
                  </span>
                ))}
              </div>
            </div>

            {/* Action buttons - Mobile-first stacked layout */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-center sm:gap-6 pt-4">
              <button
                type="button"
                onClick={onBack}
                className="flex items-center justify-center px-4 py-3 sm:px-6 text-slate-300 hover:text-white transition-colors group order-2 sm:order-1"
              >
                <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
                <span className="text-sm sm:text-base">Change Challenges</span>
              </button>

              <button
                type="submit"
                disabled={!isFormValid}
                className={`w-full sm:w-auto px-6 py-4 sm:px-8 rounded-xl font-semibold text-white transition-all duration-300 order-1 sm:order-2 text-sm sm:text-base ${
                  isFormValid
                    ? "bg-gradient-to-r from-pink-600 to-orange-600 hover:from-pink-700 hover:to-orange-700 hover:shadow-lg hover:shadow-pink-500/30 hover:scale-105"
                    : "bg-slate-600/50 cursor-not-allowed opacity-50"
                }`}
              >
                Start Cultural Analysis
              </button>
            </div>

            {/* Required fields note */}
            <div className="text-center text-slate-400 text-sm pb-4">
              <p>* Required fields</p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
